import { z } from 'zod';
import { IsoTimestamp, SessionId, UserId } from './common.js';
export const RecoveryCommand = z.enum(['reopen', 'reset', 'reattempt']);
export const RecoveryExpectedState = z.enum([
    'ready',
    'inProgress',
    'paused',
    'submitted',
    'graded',
    'flagged',
    'invalidated',
]);
export const RecoveryCorrection = z.object({
    // A delivered result is immutable. This envelope is the explicit opt-in to
    // the AIMSIS-17512 correction/reconciliation workflow.
    resultVersion: z.number().int().positive(),
    reconciliationRequired: z.literal(true),
    reason: z.string().trim().min(3).max(500),
});
export const RecoveryRequest = z.object({
    command: RecoveryCommand,
    reason: z.string().trim().min(3).max(500),
    expectedState: RecoveryExpectedState,
    expectedVersion: z.number().int().nonnegative(),
    commandId: z.string().uuid(),
    batchId: z.string().uuid().optional(),
    correction: RecoveryCorrection.optional(),
});
export const RecoveryResponse = z.object({
    commandId: z.string().uuid(),
    batchId: z.string().uuid().optional(),
    command: RecoveryCommand,
    originalSessionId: SessionId,
    replacementSessionId: SessionId.optional(),
    status: z.string(),
    actorId: UserId,
    actorRole: z.enum(['admin', 'teacher', 'proctor']),
    reason: z.string(),
    at: IsoTimestamp,
    expectedState: RecoveryExpectedState,
    expectedVersion: z.number().int().nonnegative(),
    resultingVersion: z.number().int().nonnegative(),
    sourceState: RecoveryExpectedState,
    sourceVersion: z.number().int().nonnegative(),
    resultVersion: z.number().int().positive().optional(),
    predecessorDeliveryId: z.string().min(1).max(128).optional(),
    correctionOfSessionId: SessionId.optional(),
    correctionCommandId: z.string().uuid().optional(),
    reconciliationRequired: z.boolean().default(false),
    idempotent: z.boolean(),
});
export const RecoveryAuditEvent = RecoveryResponse.extend({
    schoolId: z.string().min(1).max(64),
    quizId: z.string().min(1).max(64),
    lineageRootSessionId: SessionId.optional(),
});
export const RecoveryBlockedReason = z.enum([
    'finalized_delivery',
    'schedule_not_started',
    'schedule_expired',
    'schedule_unavailable',
    'ineligible',
    'missing_permission',
    'active_grading',
    'replacement_snapshot_unavailable',
    'stale_state',
    'wrong_quiz',
    'wrong_tenant',
    'unknown_state',
    'not_recoverable',
]);
export const RecoveryReconciliationStatus = z.enum([
    'not_required',
    'required',
    'pending',
    'reconciled',
]);
export const RecoveryAction = z.object({
    command: RecoveryCommand,
    allowed: z.boolean(),
    blockedReasons: z.array(RecoveryBlockedReason),
    correctionRequired: z.boolean().default(false),
    minimumCorrectionVersion: z.number().int().positive().optional(),
});
export const RecoveryAttempt = z.object({
    attemptId: SessionId,
    studentId: UserId,
    studentName: z.string().nullable(),
    quizId: z.string().min(1).max(64),
    quizTitle: z.string().min(1).max(200),
    state: z.string().min(1).max(32),
    resultDeliveryStatus: z.string().min(1).max(32),
    version: z.number().int().nonnegative(),
    attemptNumber: z.number().int().positive(),
});
export const RecoveryHistoryEntry = z.object({
    commandId: z.string().uuid(),
    command: RecoveryCommand,
    actorId: UserId,
    actorDisplayName: z.string().nullable(),
    reason: z.string(),
    at: IsoTimestamp,
    oldAttemptId: SessionId,
    newAttemptId: SessionId.nullable(),
    oldState: z.string().min(1).max(32),
    newState: z.string().min(1).max(32),
    reconciliationStatus: RecoveryReconciliationStatus,
});
export const RecoveryReplayEnvelope = z.object({
    attemptId: SessionId,
    commandId: z.string().uuid(),
    command: RecoveryCommand,
    reason: z.string(),
    expectedState: RecoveryExpectedState,
    expectedVersion: z.number().int().nonnegative(),
    correction: RecoveryCorrection.optional(),
    status: z.string(),
    resultingVersion: z.number().int().nonnegative(),
    idempotent: z.boolean(),
    at: IsoTimestamp,
});
export const RecoveryDetail = z.object({
    attempt: RecoveryAttempt,
    actions: z.array(RecoveryAction),
    history: z.array(RecoveryHistoryEntry),
    lastSuccessfulCommand: RecoveryReplayEnvelope.optional(),
    role: z.enum(['admin', 'teacher', 'proctor']),
});
export const RecoveryBulkPreviewRequest = z.object({
    attemptIds: z.array(SessionId).min(2).max(500),
    command: RecoveryCommand,
}).superRefine(({ attemptIds }, context) => {
    if (new Set(attemptIds).size !== attemptIds.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['attemptIds'],
            message: 'attemptIds must be unique',
        });
    }
});
export const RecoveryBulkPreviewItem = z.object({
    attemptId: SessionId,
    attempt: RecoveryAttempt.optional(),
    allowed: z.boolean(),
    blockedReasons: z.array(RecoveryBlockedReason),
});
export const RecoveryBulkPreviewResponse = z.object({
    command: RecoveryCommand,
    items: z.array(RecoveryBulkPreviewItem),
});
export const RecoveryBulkAttempt = z.object({
    attemptId: SessionId,
    studentId: UserId,
    expectedState: RecoveryExpectedState,
    expectedVersion: z.number().int().nonnegative(),
    commandId: z.string().uuid(),
});
export const RecoveryBulkRequest = z.object({
    batchId: z.string().uuid(),
    command: RecoveryCommand,
    reason: z.string().trim().min(3).max(500),
    attempts: z.array(RecoveryBulkAttempt).min(1).max(500),
}).superRefine(({ attempts }, context) => {
    if (new Set(attempts.map((attempt) => attempt.attemptId)).size !== attempts.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['attempts'],
            message: 'attemptIds must be unique',
        });
    }
    if (new Set(attempts.map((attempt) => attempt.commandId)).size !== attempts.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['attempts'],
            message: 'commandIds must be unique',
        });
    }
});
export const RecoveryBulkResult = z.object({
    attemptId: SessionId,
    status: z.enum(['success', 'skipped', 'failed']),
    reasons: z.array(RecoveryBlockedReason),
    response: RecoveryResponse.optional(),
});
export const RecoveryBulkAuditEvent = z.object({
    batchId: z.string().uuid(),
    commandId: z.string().uuid(),
    command: RecoveryCommand,
    actor: z.object({
        id: UserId,
        role: z.enum(['admin', 'teacher', 'proctor']),
    }),
    actorId: UserId,
    actorRole: z.enum(['admin', 'teacher', 'proctor']),
    timestamp: IsoTimestamp,
    at: IsoTimestamp,
    reason: z.string(),
    schoolId: z.string().min(1).max(64),
    quizId: z.string().min(1).max(64),
    studentId: UserId,
    attemptId: SessionId,
    bulkResult: z.enum(['success', 'skipped', 'failed']),
    bulkReasons: z.array(RecoveryBlockedReason),
});
export const RecoveryBulkResponse = z.object({
    batchId: z.string().uuid(),
    command: RecoveryCommand,
    results: z.array(RecoveryBulkResult),
});
//# sourceMappingURL=recovery.js.map